// 2026-06-02 (bundle A from Cowork hand-off) — client-side conversation
// filter predicates + count aggregation.
//
// Six new filter chips were added to the Chats view:
//   - Hot              (last activity within 60min, newest-first within filter)
//   - Drip Active      (is_workflow_active === true)
//   - Awaiting Reply   (last_message.direction === 'out' AND not booked/archived)
//   - Quoted / No Booking (quote_amount > 0 AND not booked/archived)
//   - Owner-Handled    (is_owner_handled === true)
//   - Stale            (last activity > 7 days ago)
//
// All chips are CLIENT-SIDE — Conversations.tsx fetches with `filter='all'`
// from the Worker and applies the predicate here. Counts are computed
// across the entire loaded conversation set so every chip shows an
// accurate badge regardless of which chip is active.
//
// The pre-existing filter values (all / needs / quoted / booked / resolved /
// called) used to be server-side; they're handled here too so the data
// flow is uniform — same single fetch, same client-side filter.

import type {
  ConversationFilter,
  ConversationSummary,
} from '@/lib/api';

const HOT_WINDOW_MS = 60 * 60_000;          // 60 minutes
const STALE_THRESHOLD_MS = 7 * 86400_000;   // 7 days
const CALLED_WINDOW_MS = 7 * 86400_000;     // 7-day rolling for the 'called' chip

function lastMessageMs(c: ConversationSummary): number {
  return Date.parse(c.last_message.timestamp_iso);
}

/**
 * Match a single conversation against a filter predicate.
 *
 * Stable, side-effect-free, deterministic — safe to call from useMemo
 * filter / map / count paths.
 */
export function matchesConversationFilter(
  c: ConversationSummary,
  filter: ConversationFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'needs':
      return c.status === 'attention';
    case 'quoted':
      return c.status === 'quoted';
    case 'booked':
      return c.status === 'booked';
    case 'resolved':
      return c.status === 'resolved';
    case 'called':
      // The original server-side 'called' filter used a rolling 7-day window
      // off last_message.source === 'shelby'. Approximated client-side as:
      // last_message marked as a voice/shelby source AND within last 7d.
      return (
        c.last_message.source === 'shelby' &&
        Date.now() - lastMessageMs(c) < CALLED_WINDOW_MS
      );
    case 'hot':
      return Date.now() - lastMessageMs(c) < HOT_WINDOW_MS;
    case 'drip_active':
      return c.is_workflow_active === true;
    case 'awaiting_reply':
      return (
        c.last_message.direction === 'out' &&
        c.status !== 'booked' &&
        c.status !== 'resolved'
      );
    case 'quoted_no_booking':
      return (
        typeof c.quote_amount === 'number' &&
        c.quote_amount > 0 &&
        c.status !== 'booked' &&
        c.status !== 'resolved'
      );
    case 'owner_handled':
      return c.is_owner_handled === true;
    case 'stale':
      return Date.now() - lastMessageMs(c) > STALE_THRESHOLD_MS;
    default:
      return true;
  }
}

/**
 * Apply a filter to a conversation list, returning the filtered + sorted
 * subset. The 'hot' chip sorts newest-first; other chips preserve input
 * order (caller controls top-level sort — pinned-first etc.).
 */
export function applyConversationFilter(
  conversations: readonly ConversationSummary[],
  filter: ConversationFilter,
): ConversationSummary[] {
  const matched = conversations.filter((c) => matchesConversationFilter(c, filter));
  if (filter === 'hot') {
    // Most-recent activity at the top within the Hot view.
    return matched.slice().sort((a, b) => lastMessageMs(b) - lastMessageMs(a));
  }
  return matched;
}

/**
 * Compute a count for every filter chip over the loaded conversation set.
 * Returned as a partial record keyed by filter value. Counts of 0 are
 * still present (Number) so FilterChips can decide whether to render
 * the badge (see its existing `showCount` logic).
 */
export function computeConversationFilterCounts(
  conversations: readonly ConversationSummary[],
): Partial<Record<ConversationFilter, number>> {
  const counts: Partial<Record<ConversationFilter, number>> = {
    all: conversations.length,
    needs: 0,
    quoted: 0,
    booked: 0,
    resolved: 0,
    called: 0,
    hot: 0,
    drip_active: 0,
    awaiting_reply: 0,
    quoted_no_booking: 0,
    owner_handled: 0,
    stale: 0,
  };
  for (const c of conversations) {
    if (matchesConversationFilter(c, 'needs')) counts.needs = (counts.needs ?? 0) + 1;
    if (matchesConversationFilter(c, 'quoted')) counts.quoted = (counts.quoted ?? 0) + 1;
    if (matchesConversationFilter(c, 'booked')) counts.booked = (counts.booked ?? 0) + 1;
    if (matchesConversationFilter(c, 'resolved')) counts.resolved = (counts.resolved ?? 0) + 1;
    if (matchesConversationFilter(c, 'called')) counts.called = (counts.called ?? 0) + 1;
    if (matchesConversationFilter(c, 'hot')) counts.hot = (counts.hot ?? 0) + 1;
    if (matchesConversationFilter(c, 'drip_active')) counts.drip_active = (counts.drip_active ?? 0) + 1;
    if (matchesConversationFilter(c, 'awaiting_reply')) counts.awaiting_reply = (counts.awaiting_reply ?? 0) + 1;
    if (matchesConversationFilter(c, 'quoted_no_booking'))
      counts.quoted_no_booking = (counts.quoted_no_booking ?? 0) + 1;
    if (matchesConversationFilter(c, 'owner_handled'))
      counts.owner_handled = (counts.owner_handled ?? 0) + 1;
    if (matchesConversationFilter(c, 'stale')) counts.stale = (counts.stale ?? 0) + 1;
  }
  return counts;
}
